import type { ComponentProps, ReactNode } from "react";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { cva } from "class-variance-authority";

import { ToastErrorIcon } from "@/assets/svg/Toast/ToastErrorIcon";
import { ToastMessageIcon } from "@/assets/svg/Toast/ToastMessageIcon";
import { ToastSuccessIcon } from "@/assets/svg/Toast/ToastSuccessIcon";
import { ToastWarningIcon } from "@/assets/svg/Toast/ToastWarningIcon";
import { cn } from "@/utils/cn";

import type { ToastVariant } from "./toastStore";

/** Оболочка toast-а: фон, рамка, тень. Основа для `Toast` и своих элементов (`ToastUpload` и т.п.). */
export function ToastSurface({ className, ...props }: ComponentProps<"div">) {
    return (
        <div
            className={cn(
                "flex w-full items-start gap-3 rounded-lg bg-surface-raised p-4 text-text-muted shadow-lg ring-1 ring-border",
                className,
            )}
            {...props}
        />
    );
}

interface ToastCloseButtonProps extends Omit<ComponentProps<"button">, "children"> {
    label?: string;
}

export function ToastCloseButton({ label = "Закрыть уведомление", className, ...props }: ToastCloseButtonProps) {
    return (
        <button
            type="button"
            aria-label={label}
            className={cn(
                "-my-1.5 ms-auto -me-1.5 inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg",
                "text-text-muted hover:bg-surface-hover hover:text-text focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:outline-none",
                className,
            )}
            {...props}
        >
            <XMarkIcon className="size-5" aria-hidden="true" />
        </button>
    );
}

const toastIconVariants = cva("inline-flex size-8 shrink-0 items-center justify-center rounded-lg", {
    variants: {
        variant: {
            success: "bg-success-soft text-success-fg",
            danger: "bg-danger-soft text-danger-fg",
            warning: "bg-warning-soft text-warning-fg",
            info: "bg-info-soft text-info-fg",
        },
    },
});

const VARIANT_ICONS = {
    success: ToastSuccessIcon,
    danger: ToastErrorIcon,
    warning: ToastWarningIcon,
    info: ToastMessageIcon,
} satisfies Record<ToastVariant, unknown>;

const VARIANT_LABELS: Record<ToastVariant, string> = {
    success: "Успешно",
    danger: "Ошибка",
    warning: "Предупреждение",
    info: "Информация",
};

export function ToastIcon({ variant }: { variant: ToastVariant }) {
    const Icon = VARIANT_ICONS[variant];
    return (
        <div className={toastIconVariants({ variant })}>
            <Icon className="size-5" />
            <span className="sr-only">{VARIANT_LABELS[variant]}:</span>
        </div>
    );
}

export interface ToastProps extends Omit<ComponentProps<"div">, "title"> {
    variant?: ToastVariant;
    title?: ReactNode;
    /** Без обработчика кнопка закрытия не показывается. */
    onClose?: () => void;
}

export function Toast({ variant = "info", title, onClose, className, children, ...props }: ToastProps) {
    return (
        <ToastSurface className={className} {...props}>
            <ToastIcon variant={variant} />
            <div className="min-w-0 flex-1 self-center text-sm wrap-break-word text-text">
                {title && <div className="mb-0.5 font-semibold text-text">{title}</div>}
                <div>{children}</div>
            </div>
            {onClose && <ToastCloseButton onClick={onClose} />}
        </ToastSurface>
    );
}
